import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import DogCard from "../components/DogCard";
import { Bell, AlertTriangle, Utensils, MapPin } from "lucide-react";
import { toast } from "sonner";

const SOURCES = [
    { key: "meat_trade", label: "Meat Trade Survivors", accent: true, icon: Utensils },
    { key: "street", label: "Street Rescues" },
    { key: "puppy_mill", label: "Puppy Mill Survivors" },
    { key: "surrender", label: "Owner Surrenders" },
    { key: "shelter_overflow", label: "Shelter Overflow" },
    { key: "abuse", label: "Abuse Survivors" },
];

const REGIONS = ["Asia", "Europe", "North America", "Oceania", "Africa", "South America"];

export default function AlertsPage() {
    const { user, loading } = useAuth();
    const [prefs, setPrefs] = useState(null);
    const [dogs, setDogs] = useState([]);
    const [saving, setSaving] = useState(false);
    const [countries, setCountries] = useState([]);

    useEffect(() => {
        if (!user) return;
        (async () => {
            const [pr, ov] = await Promise.all([
                api.get("/alerts/preferences"),
                api.get("/stats/regions"),
            ]);
            setPrefs(pr.data);
            setCountries(Array.from(new Set(ov.data.map((r) => r.country))).sort());
            const feed = await api.get("/alerts/feed");
            setDogs(feed.data.dogs || []);
            await api.post("/alerts/mark-seen");
        })();
    }, [user]);

    if (loading) return <div className="p-16 text-center text-[var(--rl-text-2)]">Loading…</div>;
    if (!user) return <Navigate to="/auth" replace />;
    if (!prefs) return <div className="p-16 text-center">Loading alerts…</div>;

    const toggleSource = (k) => setPrefs({ ...prefs, sources: prefs.sources.includes(k) ? prefs.sources.filter((x) => x !== k) : [...prefs.sources, k] });
    const toggleRegion = (k) => setPrefs({ ...prefs, regions: prefs.regions.includes(k) ? prefs.regions.filter((x) => x !== k) : [...prefs.regions, k] });
    const toggleCountry = (k) => setPrefs({ ...prefs, countries: prefs.countries.includes(k) ? prefs.countries.filter((x) => x !== k) : [...prefs.countries, k] });

    async function save() {
        setSaving(true);
        try {
            const { data } = await api.post("/alerts/preferences", {
                sources: prefs.sources || [],
                regions: prefs.regions || [],
                countries: prefs.countries || [],
                max_days_until_euthanasia: prefs.max_days_until_euthanasia,
                notify_browser: prefs.notify_browser,
                notify_email: prefs.notify_email,
                active: prefs.active,
            });
            setPrefs(data);
            const feed = await api.get("/alerts/feed");
            setDogs(feed.data.dogs || []);
            if (prefs.notify_browser && "Notification" in window && Notification.permission === "default") {
                Notification.requestPermission();
            }
            toast.success("Alert preferences saved");
        } catch { toast.error("Could not save"); }
        setSaving(false);
    }

    return (
        <div data-testid="alerts-page">
            <div className="bg-[var(--rl-cream)] border-b border-[var(--rl-border)]">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14">
                    <div className="eyebrow mb-3 inline-flex items-center gap-2"><Bell className="w-3.5 h-3.5" /> Live Rescue Alerts</div>
                    <h1 className="font-serif text-4xl lg:text-6xl tracking-tight max-w-3xl">Tell us who to save,<br />and we'll ping you the moment they land.</h1>
                    <p className="mt-4 text-[var(--rl-text-2)] max-w-2xl">Pick the rescue reasons, regions, and countries you care about. When a new at-risk dog enters the network that matches, you'll see a live alert here and in the header.</p>
                </div>
            </div>

            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 grid lg:grid-cols-12 gap-10">
                {/* Preferences */}
                <aside className="lg:col-span-4 lg:sticky lg:top-24 self-start">
                    <div className="card-warm rounded-2xl p-6" data-testid="alert-preferences">
                        <div className="eyebrow mb-4">Your alert rules</div>

                        <div className="mb-6">
                            <div className="text-sm font-semibold mb-3">By reason</div>
                            <div className="space-y-2">
                                {SOURCES.map((s) => {
                                    const active = prefs.sources.includes(s.key);
                                    return (
                                        <label key={s.key} data-testid={`pref-source-${s.key}`}
                                               className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition ${active ? (s.accent ? "border-[#4a1a1a] bg-[#fff5f3]" : "border-[var(--rl-primary)] bg-[#fff5f3]") : "border-[var(--rl-border)] hover:bg-[var(--rl-cream)]"}`}>
                                            <Checkbox checked={active} onCheckedChange={() => toggleSource(s.key)} />
                                            <span className="text-sm">{s.label}</span>
                                            {s.accent && <Utensils className="w-3.5 h-3.5 ml-auto text-[#4a1a1a]" />}
                                        </label>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="mb-6">
                            <div className="text-sm font-semibold mb-3">By region</div>
                            <div className="flex flex-wrap gap-2">
                                {REGIONS.map((r) => {
                                    const active = prefs.regions.includes(r);
                                    return (
                                        <button key={r} onClick={() => toggleRegion(r)}
                                                data-testid={`pref-region-${r.replace(/\s+/g, "-")}`}
                                                className={`px-3 py-1.5 rounded-full text-xs border ${active ? "bg-[var(--rl-primary)] text-white border-[var(--rl-primary)]" : "border-[var(--rl-border)] text-[var(--rl-text-2)] hover:bg-[var(--rl-cream)]"}`}>
                                            {r}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="mb-6">
                            <div className="text-sm font-semibold mb-3">By country (optional)</div>
                            <Select value="" onValueChange={(v) => toggleCountry(v)}>
                                <SelectTrigger className="border-[var(--rl-border)]" data-testid="pref-country-add"><SelectValue placeholder="Add a country…" /></SelectTrigger>
                                <SelectContent>
                                    {countries.filter((c) => !prefs.countries.includes(c)).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            {prefs.countries.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-3">
                                    {prefs.countries.map((c) => (
                                        <Badge key={c} className="bg-[var(--rl-cream)] text-[var(--rl-text)] hover:bg-[var(--rl-sage)] cursor-pointer" onClick={() => toggleCountry(c)}>
                                            {c} ✕
                                        </Badge>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="mb-6">
                            <label className="text-sm font-semibold">Only urgent (days to euthanasia)</label>
                            <Select value={prefs.max_days_until_euthanasia?.toString() || ""} onValueChange={(v) => setPrefs({ ...prefs, max_days_until_euthanasia: v ? parseInt(v) : null })}>
                                <SelectTrigger className="mt-2 border-[var(--rl-border)]" data-testid="pref-max-days"><SelectValue placeholder="Any" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="1">Within 1 day (critical)</SelectItem>
                                    <SelectItem value="3">Within 3 days</SelectItem>
                                    <SelectItem value="7">Within 7 days</SelectItem>
                                    <SelectItem value="14">Within 14 days</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="mb-6 space-y-2">
                            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="pref-notify-browser">
                                <Checkbox checked={prefs.notify_browser} onCheckedChange={(v) => setPrefs({ ...prefs, notify_browser: !!v })} /> Browser notifications
                            </label>
                            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="pref-active">
                                <Checkbox checked={prefs.active} onCheckedChange={(v) => setPrefs({ ...prefs, active: !!v })} /> Alerts active
                            </label>
                        </div>

                        <Button onClick={save} disabled={saving} className="btn-primary w-full rounded-full h-11" data-testid="save-alert-prefs">
                            {saving ? "Saving…" : "Save alert rules"}
                        </Button>
                    </div>
                </aside>

                {/* Live matches */}
                <section className="lg:col-span-8">
                    <div className="flex items-baseline justify-between mb-6">
                        <div>
                            <h2 className="font-serif text-3xl">Matching this now</h2>
                            <div className="text-sm text-[var(--rl-text-2)] mt-1">{dogs.length} at-risk dog{dogs.length === 1 ? "" : "s"} match your rules</div>
                        </div>
                        <Link to="/at-risk" className="text-sm text-[var(--rl-primary)] font-semibold hover:underline">See all at-risk →</Link>
                    </div>

                    {dogs.length === 0 ? (
                        <div className="card-warm rounded-2xl p-10 text-center" data-testid="alerts-empty">
                            <AlertTriangle className="w-10 h-10 text-[var(--rl-text-3)] mx-auto" />
                            <div className="font-serif text-2xl mt-3">No matching dogs right now.</div>
                            <p className="text-[var(--rl-text-2)] mt-2 text-sm max-w-md mx-auto">Try broadening your rules — remove a region, or increase the days-until-euthanasia window.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="alerts-feed">
                            {dogs.map((d) => <DogCard key={d.id} dog={d} urgent />)}
                        </div>
                    )}

                    <div className="mt-10 rounded-xl bg-[var(--rl-cream)] border border-[var(--rl-border)] p-5 text-sm text-[var(--rl-text-2)]">
                        <strong>How it works:</strong> Every 60 seconds while you're browsing, the notification bell in the header refreshes with new dogs added since you last opened this page. Enable browser notifications above to get pinged even when RescueLink is in a background tab.
                    </div>
                </section>
            </div>
        </div>
    );
}
